import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Spinner from '../components/Spinner';
import './Team.css';

function Team({ tenantId }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState('all');

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const endDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();
      
      const [usersData, teamsData, rolesData, absencesData] = await Promise.all([
        api.get('/users', tenantId),
        api.get('/teams', tenantId),
        api.get('/roles', tenantId),
        api.get(`/absences?startDate=${startDate}&endDate=${endDate}`, tenantId)
      ]);
      
      setUsers(Array.isArray(usersData) ? usersData : []);
      setTeams(Array.isArray(teamsData) ? teamsData : []);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
      setAbsences(Array.isArray(absencesData) ? absencesData : []);
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getUserAbsences = (userId) => {
    return absences.filter(a => a.userId === userId);
  };
  
  const getCurrentAbsence = (userId) => {
    const now = new Date();
    return absences.find(a => {
      if (a.userId !== userId) return false;
      const start = new Date(a.startDate);
      const end = new Date(a.endDate);
      return now >= start && now <= end;
    });
  };
  
  const getNextAbsence = (userId) => {
    const now = new Date();
    const futureAbsences = absences
      .filter(a => {
        if (a.userId !== userId) return false;
        const start = new Date(a.startDate);
        return start > now;
      })
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    return futureAbsences[0] || null;
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getRoleName = (roleId) => {
    if (!roleId) return '-';
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : '-';
  };

  const getTeamName = (teamId) => {
    if (!teamId) return '-';
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : '-';
  };

  const filteredUsers = selectedTeam === 'all' 
    ? users 
    : users.filter(u => u.teamId === selectedTeam);

  const groupedByTeam = teams.reduce((acc, team) => {
    acc[team.id] = users.filter(u => u.teamId === team.id);
    return acc;
  }, {});

  if (loading) {
    return <div className="team"><Spinner /></div>;
  }

  return (
    <div className="team">
      <div className="page-header">
        <h1>Team</h1>
        <div className="team-filter">
          <label>Team filtern:</label>
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            className="team-select"
          >
            <option value="all">Alle Teams</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedTeam === 'all' ? (
        <div className="teams-grid">
          {teams.map(team => {
            const teamUsers = groupedByTeam[team.id] || [];
            return (
              <div key={team.id} className="team-card">
                <h2>{team.name}</h2>
                {team.description && <p className="team-description">{team.description}</p>}
                {teamUsers.length === 0 ? (
                  <p className="no-members">Keine Mitglieder</p>
                ) : (
                  <div className="members-grid">
                    {teamUsers.map(user => (
                      <div 
                        key={user.id} 
                        className="member-card"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/account/${user.id}`)}
                      >
                        <div className="member-avatar">
                          {user.avatar ? (
                            <img src={user.avatar} alt={user.username} />
                          ) : (
                            <div className="avatar-placeholder">
                              {user.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="member-info">
                          <div className="member-name">{user.username}</div>
                          <div className="member-role">{getRoleName(user.roleId)}</div>
                          {(() => {
                            const currentAbsence = getCurrentAbsence(user.id);
                            const nextAbsence = getNextAbsence(user.id);
                            if (currentAbsence) {
                              return (
                                <div style={{ fontSize: '0.85em', color: '#f57c00', marginTop: '4px' }}>
                                  Abwesend bis {formatDate(currentAbsence.endDate)}
                                </div>
                              );
                            } else if (nextAbsence) {
                              return (
                                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                  Nächste Abwesenheit: {formatDate(nextAbsence.startDate)}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {users.filter(u => !u.teamId).length > 0 && (
            <div className="team-card">
              <h2>Ohne Team</h2>
              <div className="members-grid">
                {users.filter(u => !u.teamId).map(user => (
                  <div 
                    key={user.id} 
                    className="member-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/users?userId=${user.id}`)}
                  >
                    <div className="member-avatar">
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.username} />
                      ) : (
                        <div className="avatar-placeholder">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                      <div className="member-info">
                        <div className="member-name">{user.username}</div>
                        <div className="member-role">{getRoleName(user.roleId)}</div>
                        {(() => {
                          const currentAbsence = getCurrentAbsence(user.id);
                          const nextAbsence = getNextAbsence(user.id);
                          if (currentAbsence) {
                            return (
                              <div style={{ fontSize: '0.85em', color: '#f57c00', marginTop: '4px' }}>
                                Abwesend bis {formatDate(currentAbsence.endDate)}
                              </div>
                            );
                          } else if (nextAbsence) {
                            return (
                              <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                Nächste Abwesenheit: {formatDate(nextAbsence.startDate)}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="team-detail">
          {(() => {
            const team = teams.find(t => t.id === selectedTeam);
            const teamUsers = filteredUsers;
            return (
              <>
                <h2>{team?.name || 'Unbekanntes Team'}</h2>
                {team?.description && <p className="team-description">{team.description}</p>}
                <div className="members-grid">
                  {teamUsers.map(user => (
                    <div 
                      key={user.id} 
                      className="member-card"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/users?userId=${user.id}`)}
                    >
                      <div className="member-avatar">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.username} />
                        ) : (
                          <div className="avatar-placeholder">
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="member-info">
                        <div className="member-name">{user.username}</div>
                        <div className="member-role">{getRoleName(user.roleId)}</div>
                        {(() => {
                          const currentAbsence = getCurrentAbsence(user.id);
                          const nextAbsence = getNextAbsence(user.id);
                          if (currentAbsence) {
                            return (
                              <div style={{ fontSize: '0.85em', color: '#f57c00', marginTop: '4px' }}>
                                Abwesend bis {formatDate(currentAbsence.endDate)}
                              </div>
                            );
                          } else if (nextAbsence) {
                            return (
                              <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                Nächste Abwesenheit: {formatDate(nextAbsence.startDate)}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default Team;

